/**
 * Shared WebGL setup for pack opening and the duel overlays.
 *
 * A transparent canvas with the default premultiplied alpha and
 * `high-performance` composites as solid white on some Android GPUs.
 * Phones also skip antialias, shadows, and the full device pixel ratio.
 */

import { PCFShadowMap, SRGBColorSpace, type WebGLRenderer } from 'three'

const COARSE_MQ = '(pointer: coarse)'

export function webglOnPhone(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(COARSE_MQ).matches
}

export function webglRendererOptions(
  canvas: HTMLCanvasElement,
  antialias?: boolean,
): {
  canvas: HTMLCanvasElement
  alpha: true
  antialias: boolean
  premultipliedAlpha: false
  powerPreference: 'default'
} {
  return {
    canvas,
    alpha: true,
    antialias: antialias ?? !webglOnPhone(),
    premultipliedAlpha: false,
    powerPreference: 'default',
  }
}

/**
 * Pixel ratio, clear color, and shadows. `desktopCap` keeps the previous
 * desktop limit (packs used 2). Omit it to leave Three's own ratio.
 */
export function applyWebglBudget(
  renderer: WebGLRenderer,
  desktopCap?: number,
): void {
  const dpr = window.devicePixelRatio || 1
  if (webglOnPhone()) renderer.setPixelRatio(Math.min(dpr, 1.5))
  else if (desktopCap != null) renderer.setPixelRatio(Math.min(dpr, desktopCap))
  renderer.setClearColor(0x000000, 0)
  renderer.outputColorSpace = SRGBColorSpace
  const phone = webglOnPhone()
  renderer.shadowMap.enabled = !phone
  if (!phone) renderer.shadowMap.type = PCFShadowMap
}

/** Fires once. Does not call preventDefault: the canvas is abandoned. */
export function listenWebglLost(
  canvas: HTMLCanvasElement,
  onLost: () => void,
): () => void {
  const handler = () => onLost()
  canvas.addEventListener('webglcontextlost', handler)
  return () => canvas.removeEventListener('webglcontextlost', handler)
}
